/**
 * Dialog used by the Phase detail panel to link the phase to one Backlog
 * **parent issue**. A phase can hold multiple links — the caller passes
 * the set of already-linked `${projectKey}:${parentIssueKey}` entries via
 * `existingParentKeys` so we can warn on duplicates.
 *
 * Form:
 *   1. Choose a Backlog project (filtered to the project's configured keys).
 *   2. Type the parent issue key (e.g. APG-5639).
 *   3. Optionally specify forced 領域 values (comma-separated) — used for
 *      Backlog projects whose categories are not Migratus 領域 (e.g. APG).
 *
 * The "連携する" button calls `linkPhaseToBacklogParentIssue`, which
 * validates the parent issue exists, then fans out into per-child-issue
 * WBS task creates. The result reports how many child issues were taken in.
 */
import { useEffect, useMemo, useState } from 'react';
import { formatBacklogProjectLabel } from '@mct/shared';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { Input } from '../ui/Input';
import { useProject } from '../../hooks/useFirestoreData';
import {
  linkPhaseToBacklogParentIssue,
  type LinkPhaseToBacklogParentIssueResult,
} from '../../lib/backlogParentIssueLink';

function parseForceSystems(raw: string): string[] {
  return raw
    .split(/[,、]/)
    .map((s) => s.trim())
    .filter((s, i, arr) => s.length > 0 && arr.indexOf(s) === i);
}

export function PhaseBacklogParentIssueLinkDialog({
  open,
  projectId,
  phaseId,
  phaseName,
  existingParentKeys,
  onClose,
  onLinked,
}: {
  open: boolean;
  projectId: string;
  phaseId: string;
  phaseName: string;
  /** Set of `${projectKey}:${parentIssueKey}` already linked on this phase. */
  existingParentKeys?: ReadonlySet<string>;
  onClose: () => void;
  onLinked: (result: LinkPhaseToBacklogParentIssueResult) => void;
}) {
  const [selectedProjectKey, setSelectedProjectKey] = useState<string>('');
  const [parentIssueKey, setParentIssueKey] = useState<string>('');
  const [forceSystemsRaw, setForceSystemsRaw] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedProjectKey('');
      setParentIssueKey('');
      setForceSystemsRaw('');
      setBusy(false);
      setErrorMsg(null);
    }
  }, [open, phaseId]);

  const { data: project } = useProject(open ? projectId : null);
  const backlogProjects = useMemo(() => {
    const keys = project?.backlog?.backlogProjectKeys ?? [];
    const names = project?.backlog?.backlogProjectNames ?? {};
    return keys
      .map((key) => ({ key, name: names[key] ?? '' }))
      .slice()
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [project]);

  const trimmedKey = parentIssueKey.trim().toUpperCase();
  const isDuplicate = useMemo(() => {
    if (!selectedProjectKey || !trimmedKey) return false;
    return existingParentKeys?.has(`${selectedProjectKey}:${trimmedKey}`) === true;
  }, [existingParentKeys, selectedProjectKey, trimmedKey]);

  // Light client-side shape check — the server is the source of truth, but
  // catch the obvious "wrong project prefix" / empty cases early.
  const keyPrefixOk =
    !selectedProjectKey || !trimmedKey || trimmedKey.startsWith(`${selectedProjectKey}-`);

  const canSubmit =
    !busy &&
    selectedProjectKey.length > 0 &&
    trimmedKey.length > 0 &&
    keyPrefixOk &&
    !isDuplicate;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      const result = await linkPhaseToBacklogParentIssue({
        projectId,
        phaseId,
        backlogProjectKey: selectedProjectKey,
        parentIssueKey: trimmedKey,
        forceSystems: parseForceSystems(forceSystemsRaw),
      });
      if (!result.ok) {
        setErrorMsg(result.message);
        setBusy(false);
        return;
      }
      onLinked(result);
      onClose();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? () => undefined : onClose}
      title={`「${phaseName}」と Backlog 親課題を連携`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            キャンセル
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {busy ? (
              <span className="inline-flex items-center gap-1.5">
                <Spinner className="h-3.5 w-3.5" /> 取り込み中…
              </span>
            ) : (
              '連携する'
            )}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-xs text-slate-500">
          指定した親課題の子課題を、フェーズ直下の WBS タスクとして自動で取り込みます。子課題が追加されると日次同期で自動的に反映されます。
        </p>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">
            Backlog プロジェクト
          </label>
          <select
            className="input"
            value={selectedProjectKey}
            disabled={busy}
            onChange={(e) => setSelectedProjectKey(e.target.value)}
            data-testid="phase-parent-link-project"
          >
            <option value="">— プロジェクトを選択 —</option>
            {backlogProjects.map((p) => (
              <option key={p.key} value={p.key}>
                {formatBacklogProjectLabel(p.key, p.name)}
              </option>
            ))}
          </select>
          {project && backlogProjects.length === 0 ? (
            <p className="mt-1 text-[11px] text-amber-700">
              連携プロジェクトが未設定です。プロジェクト設定で Backlog
              プロジェクトを 1 件以上選択してください。
            </p>
          ) : null}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">
            親課題キー
          </label>
          <Input
            value={parentIssueKey}
            disabled={busy || !selectedProjectKey}
            placeholder={selectedProjectKey ? `${selectedProjectKey}-5639` : 'APG-5639'}
            onChange={(e) => setParentIssueKey(e.target.value)}
            data-testid="phase-parent-link-key"
          />
          {!keyPrefixOk ? (
            <p className="mt-1 text-[11px] text-amber-700">
              課題キーは「{selectedProjectKey}-」で始まる必要があります。
            </p>
          ) : null}
          {isDuplicate ? (
            <p className="mt-1 text-[11px] text-amber-700">
              その親課題は既にこのフェーズに連携されています。
            </p>
          ) : null}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">
            領域を強制指定（任意）
          </label>
          <Input
            value={forceSystemsRaw}
            disabled={busy}
            placeholder="例: MDM, 会計（カンマ区切り）"
            onChange={(e) => setForceSystemsRaw(e.target.value)}
            data-testid="phase-parent-link-force-systems"
          />
          <p className="mt-1 text-[11px] text-slate-400">
            Backlog
            のカテゴリが領域に対応しないプロジェクト（例: APG）向け。指定すると取り込む全行の領域がこの値で固定されます。空欄なら通常のカテゴリ→領域マッピングを使用します。
          </p>
        </div>
        {errorMsg ? (
          <p className="text-[11px] text-red-600" data-testid="phase-parent-link-error">
            {errorMsg}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
